/**
 * Shared constants for toolbar components
 *
 * These values are used by both standard and Procore toolbar implementations.
 * Colors use Procore design tokens where available, with custom values for
 * divergent design requirements.
 */

import { colors, spacing } from "@procore/core-react";

// =============================================================================
// Colors (shared across all toolbar variants)
// =============================================================================
export const TOOLBAR_COLORS = {
    ACTIVE: colors.blue50,
    DEFAULT: colors.gray15,
    HOVER: colors.gray90,
    /** Custom - no exact Procore match */
    HOVER_DARK: "#d0d4d8",
    ACTIVE_BG: colors.blue96,
    /** Custom - no exact Procore match */
    ACTIVE_HOVER: "#BCD1F5",
    DISABLED: colors.gray50,
    /** View Modifications badge + pill */
    MODIFICATION_ORANGE: colors.orange50,
    MODIFICATION_ORANGE_TEXT: colors.white,
    /** View Modifications flyout — header and body text */
    FLYOUT_TEXT: colors.gray15,
    /** View Modifications flyout — reset button states */
    FLYOUT_BUTTON_BG: colors.gray90,
    /** Custom - no exact Procore match */
    FLYOUT_BUTTON_BG_HOVER: "#D6DADC",
    /** Custom - no exact Procore match */
    FLYOUT_BUTTON_BG_ACTIVE: "#C2CAD0",
    /** View Modifications flyout — filter token row */
    TOKEN_ROW_BG: colors.blue96,
    /** Custom - no exact Procore match */
    TOKEN_ROW_BG_HOVER: "#D9E6FB",
    /** View Modifications flyout — token text and clear icon */
    TOKEN_TEXT: colors.blue45,
    /** View Modifications flyout — toggle switch states */
    TOKEN_TOGGLE_ON: colors.blue50,
    /** Custom - no exact Procore match */
    TOKEN_TOGGLE_OFF: "#8B98A1",
    /** Custom - no exact Procore match */
    TOKEN_CLEAR_HOVER: "#AABFE8",
    /** Custom - action-specific */
    SAVE_GREEN: "#4CAF50",
    SAVE_BG: "#E8F5E9",
    EXIT_RED: "#F44336",
    EXIT_BG: "#FFEBEE",
    /** Custom - no exact Procore match */
    EXPANDER_ACTIVE: "#1952B3",
    EXPANDER_DEFAULT: "#6A767C",
    WHITE: colors.white,
    BLACK: colors.black,
    LIGHT_GREY: colors.gray96,
    BORDER: colors.gray90,
    BADGE_BG: colors.blue98,
    /** Custom - no exact Procore match */
    BADGE_TEXT: "#1976d2",
    /** Custom - slightly lighter than gray90; tooltip resting state background */
    TOOLTIP_BG: "#e0e3e5",
    TOOLTIP_HOVERED_BG: colors.white,
    /** Custom - between gray70 and gray85; shortcut key pill background */
    TOOLTIP_PILL_BG: "#c1cbd0",
    TOOLTIP_TEXT: colors.gray15,
    TOOLTIP_SHADOW: "0 6px 20px rgba(0, 0, 0, 0.24)",
    TOOLTIP_FONT_FAMILY: "Inter, sans-serif",
    PANEL_BG: colors.gray96,
    /** Custom - no exact Procore match */
    FLYOUT_GREY: "#C3C3C3",
} as const;

// =============================================================================
// Button sizing (standard toolbar buttons)
// =============================================================================
export const TOOLBAR_BUTTON = {
    SIZE: 36,
    RADIUS: spacing.xs,
    ICON_SIZE: 24,
    PADDING: 6,
} as const;

// =============================================================================
// Toolbar container styling
// =============================================================================
export const TOOLBAR_CONTAINER = {
    BG: colors.white,
    BORDER: `1px solid ${colors.gray90}`,
    RADIUS: 8,
    SHADOW: "0px 0px 4px 0px #00000040",
    PADDING: spacing.xs,
    GAP: spacing.xs,
    WIDTH: 44,
} as const;

// =============================================================================
// Procore-specific: Bottom toolbar entry sizing (from Figma)
// =============================================================================
export const PROCORE_BOTTOM_ENTRY = {
    WIDTH: 56,
    ICON_SIZE: 48,
    TEXT_WIDTH: 56,
    TEXT_HEIGHT: 16,
    TOOLBAR_HEIGHT: 72,
    GAP: 2,
} as const;

// =============================================================================
// Procore-specific: Top bar sizing
// =============================================================================
export const PROCORE_TOP_BAR = {
    HEIGHT: 64,
    HEIGHT_PORTRAIT: 80, // Taller in phone portrait due to vertical dropdown layout
    CLOSE_SIZE: 40,
    NAME_WIDTH: 213,
    REVISION_WIDTH: 236,
    ACTION_SIZE: 40,
    /** 20% opacity white overlay */
    BG: "rgba(255, 255, 255, 0.2)",
    /** 60% opacity white for frosted glass control boxes */
    CONTROL_BG: "rgba(255, 255, 255, 0.6)",
    /** Solid white background for the close button */
    CLOSE_BG: "#FFFFFF",
    /** Light grey background for the model name box */
    NAME_BG: "#E3E6E8",
} as const;

// =============================================================================
// Procore-specific: Undo/Redo group sizing
// =============================================================================
export const PROCORE_UNDO_GROUP = {
    WIDTH: 164,
    HEIGHT: 66,
    GAP: spacing.xs,
    BUTTON_WIDTH: 52,
    BUTTON_HEIGHT: 66,
} as const;

// =============================================================================
// Layout constants
// =============================================================================
export const TOOLBAR_LAYOUT = {
    MARGIN_MOBILE: 8,
    MARGIN_TABLET: 16,
    GROUP_GAP: spacing.sm,
} as const;

// =============================================================================
// Joysticks layout constants (4 is knob outline size)
// =============================================================================
export const JOYSTICKS_LAYOUT = {
    MARGIN_TABLET: 32 + 4,
    MARGIN_MOBILE: 8 + 4,
    MARGIN_BOTTOM_MOBILE: 8 + 4,
    MARGIN_BOTTOM_TABLET_LANDSCAPE: 24,
    MARGIN_BOTTOM_TABLET_PORTRAIT: 144,
    MARGIN_BOTTOM_TABLET_PORTRAIT_MIN_WIDTH: 700,
    /** Height of one joystick control (quad or bi-directional). */
    HEIGHT: 94,
    /** Width of the quad (4-directional) joystick — left joystick on mobile. */
    QUAD_WIDTH: 94,
    /** Width of the bi-directional joystick — right joystick on mobile. */
    BI_WIDTH: 64,
} as const;

// =============================================================================
// Axis sphere (navigation sphere) layout constants
// =============================================================================
export const AXIS_SPHERE_LAYOUT = {
    /** Axis sphere diameter on phone (px). */
    PHONE_SIZE: 100,
    /** Axis sphere diameter on tablet (px). */
    TABLET_SIZE: 130,
    /** Axis sphere diameter on desktop (px). */
    DESKTOP_SIZE: 160,
    /** Margin from viewport edge on desktop (px). */
    DESKTOP_MARGIN: 16,
} as const;

// =============================================================================
// Z-indices
// =============================================================================
export const TOOLBAR_Z_INDEX = {
    LARGE_MAP_WEB: 1110,
    TOP_BAR: 1060,
    LARGE_MAP_MOBILE: 1055,
    /** Mobile bottom toolbar — above UndoRedo, below large map, drawers, and top bar. */
    BOTTOM_TOOLBAR: 1052,
    TOOLBAR: 1050,
    TOOLTIP: 1051,
    SIDE_PANEL: 1040,
} as const;

// =============================================================================
// Animation
// =============================================================================
export const TOOLBAR_ANIMATION = {
    DURATION: 200,
    EASING: "ease-out",
    DURATION_OPEN: 300,
    EASING_OPEN: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

// =============================================================================
// Minimap layout constants
// =============================================================================
export const MINIMAP_LAYOUT = {
    MINIMAP_MIN_HEIGHT: 360,
    Z_INDEX: 1050,
    /** Desktop minimap canvas size. */
    WEB_WIDTH: 240,
    WEB_HEIGHT: 172,
    /** Desktop minimap offset from the bottom-right corner. */
    WEB_MARGIN: 16,
    /** Mobile minimap container footprint below the top bar (92px canvas + home button + padding). */
    MOBILE_WIDTH: 158,
    MOBILE_HEIGHT: 108,
} as const;

export const QUICK_ACCESS_TOOLBAR = {
    FLYOUT_Z_INDEX: 1100,
} as const;

// =============================================================================
// Bottom nav toolbar layout (desktop bottom controller)
// =============================================================================
export const BOTTOM_NAV_LAYOUT = {
    /** Pixel gap between the toolbar pill and the viewport bottom edge. */
    BOTTOM_OFFSET: 16,
    /** Full bottom reservation: toolbar height (button + vertical padding) + bottom offset. */
    RESERVED_HEIGHT: TOOLBAR_BUTTON.SIZE + 2 * TOOLBAR_CONTAINER.PADDING + 16,
} as const;

// =============================================================================
// Offline indicator layout (desktop top hint)
// =============================================================================
export const OFFLINE_INDICATOR_LAYOUT = {
    /** Banner height when visible: 4px padding + 24px content + 4px padding. */
    HEIGHT: 32,
} as const;
